import React, { useEffect } from 'react';
import { Placeholder, RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import { useDispatch, useSelector } from 'react-redux';
import ReplaceableText from '../../../utils/text/ReplaceableText';
import {
  updatePaymentDay,
  updateShowPreferredPaymentDaySelector,
} from '../../../redux/slices/quoteDataSlice';
import { getOrdinal } from '../../../utils/dateUtils/dateUtils';
import PropTypes from 'prop-types';
import { MAX_PAYMENT_DATE } from '../../questions/PaymentDayDropDown';

const QuoteSummaryDetails = ({ fields, params, rendering }) => {
  let { outerClass, innerClass, headerClass, legalTextClass } = setupVariantClasses(params);

  const dispatch = useDispatch();
  let dependents = null;
  const quoteResponse = useSelector(state => state.quoteResponse);
  const quoteData = useSelector(state => state.quoteData);
  if (quoteResponse?.prospectDetails?.dependents) {
    dependents = quoteResponse.prospectDetails.dependents;
  }
  if (!dependents && quoteData?.prospectDetails?.dependents) {
    dependents = quoteData.prospectDetails.dependents;
  }
  const quoteReference = quoteData?.selectedQuote?.healthQuoteNumber;
  const coverStartDate = quoteData?.prospectDetails?.coverStartDate;
  const productName = quoteData?.selectedQuote?.productName;
  const showPreferredPaymentDaySelector =
    quoteData?.prospectDetails?.showPreferredPaymentDaySelector;
  const paymentDay = quoteData?.prospectDetails?.bankDetails?.paymentDay;
  const displayPaymentDay = getOrdinal(paymentDay);
  const togglePaymentDaySelector = () => {
    dispatch(updateShowPreferredPaymentDaySelector(!showPreferredPaymentDaySelector));
  };

  const renderToCoverValue = () => {
    const { spouse, children } = dependents || {};
    if (children?.length > 0) {
      const childString = `${children.length} child${children.length > 1 ? 'ren' : ''}`;
      return spouse ? `You, a partner and ${childString}` : `You and ${childString}`;
    }
    return spouse ? 'You and a partner' : 'You';
  };

  useEffect(() => {
    if (!quoteData?.prospectDetails?.bankDetails?.paymentDay) {
      let paymentDay = Number(coverStartDate?.split('/')[0]);
      if (paymentDay > MAX_PAYMENT_DATE) {
        paymentDay = MAX_PAYMENT_DATE;
      }
      dispatch(updatePaymentDay(paymentDay));
    }
  }, [quoteData?.prospectDetails?.bankDetails?.paymentDay]);

  let containerInner = renderInnerContainer(
    headerClass,
    fields,
    legalTextClass,
    productName,
    quoteReference,
    coverStartDate,
    renderToCoverValue,
    displayPaymentDay,
    togglePaymentDaySelector
  );

  let placeholders;

  if (rendering?.placeholders && rendering.placeholders['quoteSummaryDetails-richText']) {
    placeholders = <Placeholder name="quoteSummaryDetails-richText" rendering={rendering} />;
  }

  if (innerClass) {
    containerInner = <div className={innerClass}>{containerInner}</div>;
  }

  if (outerClass) {
    return (
      <div className={outerClass}>
        {containerInner}
        {placeholders}
      </div>
    );
  } else {
    return (
      <>
        {containerInner}
        {placeholders}
      </>
    );
  }
};

const renderInnerContainer = (
  headerClass,
  fields,
  legalTextClass,
  productName,
  quoteReference,
  coverStartDate,
  renderToCoverValue,
  displayPaymentDay,
  togglePaymentDaySelector
) => {
  return (
    <div className="vds-quote-details-container__inner">
      <div className="vds-quote-details-container__heading">
        <ReplaceableText
          tag="h3"
          className={headerClass}
          field={fields.heading}
          data-testid={`${fields.testid?.value}-heading`}
        />
        <ReplaceableText
          tag="p"
          className={legalTextClass}
          field={fields.legalText}
          data-testid={`${fields.testid?.value}-legaltext`}
        />
      </div>
      <dl className="vds-quote-details-container__specification">
        {fields.productNameLabel?.value && (
          <>
            <Text
              tag="dt"
              field={fields.productNameLabel}
              data-testid={`${fields.testid?.value}-productnamelabel`}
            />
            <dd data-testid={`${fields.testid?.value}-productnamevalue`}>
              {productName || '{productName}'}
            </dd>
          </>
        )}
        {fields.quoteReferenceLabel?.value && (
          <>
            <Text
              tag="dt"
              field={fields.quoteReferenceLabel}
              data-testid={`${fields.testid?.value}-quotereflabel`}
            />
            <dd data-testid={`${fields.testid?.value}-quoterefvalue`}>
              {quoteReference || '{quoteReference}'}
            </dd>
          </>
        )}
        {fields.coverStartDateLabel?.value && (
          <>
            <Text
              tag="dt"
              field={fields.coverStartDateLabel}
              data-testid={`${fields.testid?.value}-coverstartlabel`}
            />
            <dd data-testid={`${fields.testid?.value}-coverstartvalue`}>{coverStartDate}</dd>
          </>
        )}
        {fields.toCoverLabel?.value && (
          <>
            <Text
              tag="dt"
              field={fields.toCoverLabel}
              data-testid={`${fields.testid?.value}-tocoverlabel`}
            />
            <dd data-testid={`${fields.testid?.value}-tocovervalue`}>{renderToCoverValue()}</dd>
          </>
        )}
        {fields.paymentDayLabel?.value && fields.paymentDayText?.value && (
          <>
            <Text
              tag="dt"
              field={fields.paymentDayLabel}
              data-testid={`${fields.testid?.value}-paymentDayLabel`}
            />
            <dd data-testid={`${fields.testid?.value}-paymentDayText`}>
              {displayPaymentDay}
              {fields.paymentDayText?.value}
            </dd>
            <dt></dt>
            <dd>
              <button
                type="button"
                className="vds-link"
                onClick={e => {
                  e.preventDefault();
                  togglePaymentDaySelector();
                }}
                data-testid={`${fields.testid?.value}-link`}
              >
                {fields?.changePaymentDateText?.value}
              </button>
            </dd>
          </>
        )}
      </dl>
      <div>
        <Text
          tag="h4"
          className="vds-quote-details-container__subheading"
          field={fields.subHeading}
          data-testid={`${fields.testid?.value}-subheading`}
        />
        {fields.overview?.value && (
          <RichText
            tag="p"
            field={fields.overview}
            data-testid={`${fields.testid?.value}-overview`}
          />
        )}
      </div>
    </div>
  );
};

const setupVariantClasses = function (params) {
  const variant = params?.variant || {};
  const outerClass = variant === 'your-quote' ? 'vds-grid__column vds-grid__column--lg-4' : '';
  let innerClass = '';
  let headerClass = 'vds-quote-details-container__quote-primary';
  let legalTextClass = 'vds-quote-details-container__legal-text';
  if (variant === 'your-quote') {
    innerClass = 'vds-quote-details-container';
  }
  if (variant === 'direct-debit') {
    innerClass = 'vds-form__group';
    headerClass = 'vds-type-display-2b vds-margin-bottom-spacing-0';
    legalTextClass = 'vds-type-body-small vds-colour-light-black vds-margin-bottom-spacing-4';
  }
  return { outerClass, innerClass, headerClass, legalTextClass };
};

QuoteSummaryDetails.propTypes = {
  coverStartDate: PropTypes.shape({
    date: PropTypes.string,
  }),
  showPreferredPaymentDaySelector: PropTypes.bool,
  productName: PropTypes.string,
  paymentDay: PropTypes.number,
  rendering: PropTypes.object,
  fields: PropTypes.object,
  params: PropTypes.object,
};

export { QuoteSummaryDetails };
export default QuoteSummaryDetails;
